/* Complements the existing CartDrawer class; no changes to cart-drawer.js. */
if (!customElements.get('amata-cart-cross-sell')) {
  customElements.define('amata-cart-cross-sell', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.removeEventListener('click', this.onClick);
    }

    async loadRecommendations() {
      const signal = this.controller.signal;
      const inCart = new Set((this.dataset.productIds || '').split(',').filter(Boolean));
      const seen = new Set(inCart);
      const cards = [];

      // Cart order determines priority. Stop after finding two distinct products.
      for (const productId of inCart) {
        if (signal.aborted || cards.length >= 2) break;
        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('product_id', productId);
          url.searchParams.set('section_id', 'amata-cart-recommendations');
          url.searchParams.set('intent', 'complementary');
          url.searchParams.set('limit', '10');
          const response = await fetch(url, { signal, credentials: 'same-origin' });
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          for (const card of html.querySelectorAll('[data-amata-recommendation]')) {
            const id = card.dataset.productId;
            if (!id || seen.has(id)) continue;
            seen.add(id);
            cards.push(document.importNode(card, true));
            if (cards.length >= 2) break;
          }
        } catch (error) {
          if (signal.aborted) return;
          // A failed recommendation request must not interrupt shopping.
        }
      }

      if (signal.aborted || !this.isConnected) return;
      this.querySelector('[data-amata-list]').replaceChildren(...cards);
      this.hidden = cards.length === 0;
    }

    async handleClick(event) {
      const button = event.target.closest('[data-amata-add]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (this.adding) return;
      this.adding = true;
      const errorElement = this.querySelector('[data-amata-error]');
      errorElement.textContent = '';
      const originalText = button.textContent;
      button.textContent = this.dataset.addingLabel;
      this.querySelectorAll('[data-amata-add]').forEach((item) => { item.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      const drawer = this.closest('cart-drawer');
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: button.dataset.variantId, quantity: 1 }],
            sections: drawer.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(typeof state.description === 'string' ? state.description : this.dataset.errorLabel);
        }

        // The add has succeeded. Never retry it if section rendering fails.
        try {
          const sections = drawer.getSectionsToRender();
          if (!sections.every((section) => typeof state.sections?.[section.id] === 'string')) {
            throw new Error('Missing cart sections');
          }
          drawer.setActiveElement(document.getElementById('cart-icon-bubble'));
          drawer.renderContents({ ...state, id: button.dataset.variantId });
          requestAnimationFrame(() => {
            if (drawer.classList.contains('active')) drawer.querySelector('.drawer__close')?.focus();
          });
        } catch (error) {
          window.location.assign(root + 'cart');
        }
      } catch (error) {
        if (this.isConnected) errorElement.textContent = error.message || this.dataset.errorLabel;
      } finally {
        this.adding = false;
        if (this.isConnected) {
          button.textContent = originalText;
          this.querySelectorAll('[data-amata-add]').forEach((item) => { item.disabled = false; });
          this.removeAttribute('aria-busy');
        }
      }
    }
  });
}
